import { notifySplashGlideLanded, clearSplashGlideLanded } from './splashHandoff';
import { cancelLogoJourney, cancelLogoJourneyLeave, flyLogoJourney, LOGO_JOURNEY_LEG_MS } from './logoJourney';

/**
 * Splash → screen wordmark glide (KAN-562) — the splash end of the ONE logo
 * journey (logoJourney.js owns the element and its motion).
 *
 * As the splash starts to leave, the journey element — already resting on the
 * splash wordmark after the sign-in morph, or created there pixel-for-pixel on
 * a cold start — flies, ONE transform (translate + scale) on the compositor,
 * onto the screen's own wordmark (the element marked
 * `data-cvx-splash-glide-target`). The splash itself only fades (opacity). The
 * target stays hidden until the element lands, then is revealed in the same
 * task that removes the element.
 *
 * Rules (AGENTS anti-patterns): only the small journey element carries
 * `will-change`; nothing full-screen is promoted. A landing never waits on an
 * animation event alone (a timeout backstops it). No Web Animations API → no
 * glide, the target is revealed at once (the splash fade covers it = a plain
 * fade).
 */

export const SPLASH_GLIDE_ATTR = 'data-cvx-splash-glide-target';
/**
 * Set by a destination that already settled its wordmark UNDER the opaque
 * splash (forced MFA gate — RD-SEC-021b/c). The leave handoff skips its
 * second settle wait so the logo flies the instant the splash is free to
 * leave — no double-pause. DESKTOP ONLY: touch destinations (phone/tablet /
 * PWA) must NOT set this — the touch band still recenters the card, and
 * welcome keeps the leave-side settle for that reflow.
 */
export const SPLASH_GLIDE_PRESETTLED_ATTR = 'data-cvx-splash-glide-presettled';
/**
 * The welcome screens' decorative beats (the guide's ready pill) keep the
 * original glide-family curve; the logo's own legs use LOGO_JOURNEY_EASE.
 */
export const SPLASH_GLIDE_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
/** Default glide length (role welcome, mockup 2A); a target can ask for its own via the attribute value. */
export const SPLASH_GLIDE_DEFAULT_MS = LOGO_JOURNEY_LEG_MS;
/** The splash's own fade while a glide runs (mockup: 380ms, 40ms after the glide starts). */
export const SPLASH_GLIDE_FADE_MS = 380;
export const SPLASH_GLIDE_FADE_DELAY_MS = 40;

export function prefersReducedMotion() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (_e) {
    return false;
  }
}

export function findSplashGlideTarget() {
  if (typeof document === 'undefined') return null;
  return document.querySelector(`[${SPLASH_GLIDE_ATTR}]`);
}

function glideDurationOf(target) {
  const v = Number(target?.getAttribute?.(SPLASH_GLIDE_ATTR));
  return Number.isFinite(v) && v > 0 ? v : SPLASH_GLIDE_DEFAULT_MS;
}

/**
 * Where the element will REST. Normally that is its painted rect, exact to the
 * sub-pixel (the landing swaps on that box). When an ancestor is still
 * sliding/scaling in, the painted rect is mid-motion, so the offset chain
 * (transforms ignored; each offsetParent's border added back) gives the rest
 * position instead — whole pixels, but in the right place. Falls back to the
 * painted rect when the chain is unusable or disagrees wildly (a transformed
 * containing block, a scrolled ancestor).
 */
export function layoutRect(el) {
  const painted = el.getBoundingClientRect();
  let x = 0;
  let y = 0;
  let n = el;
  while (n) {
    x += n.offsetLeft || 0;
    y += n.offsetTop || 0;
    n = n.offsetParent;
    if (n) { x += n.clientLeft || 0; y += n.clientTop || 0; }
  }
  const w = el.offsetWidth || painted.width;
  const h = el.offsetHeight || painted.height;
  const drift = Math.abs(x - painted.left) + Math.abs(y - painted.top);
  // At rest (offsets are whole pixels, so allow their rounding): the painted rect is exact.
  if (!w || !h || drift <= 2 || drift > 64) return { left: painted.left, top: painted.top, width: painted.width, height: painted.height };
  return { left: x, top: y, width: w, height: h };
}

/**
 * Cancel the glide in flight, if any (the splash re-covered): the journey
 * element is removed NOW and its landing dropped. The target is not revealed —
 * the splash is back over it; its own phase rules reveal it on the next leave.
 * A journey still arriving from the sign-in card (leg 1) or resting on the
 * splash is kept. Idempotent.
 */
export function cancelSplashGlide() {
  cancelLogoJourneyLeave();
}

/** Sign-out (clearPerUserDeviceState / clearSignedOutMemoryState): no glide, no retained DOM node survives the session. */
export function resetSplashGlideSession() {
  cancelLogoJourney();
  clearSplashGlideLanded();
}

/**
 * Fly the logo onto `targetEl` — leg 2 of the ONE logo journey (logoJourney):
 * from wherever the journey element rests (it came from the sign-in card), or,
 * on a cold start, from the splash wordmark `sourceEl` itself. Returns the
 * glide length in ms, or 0 when no glide ran (the target is then revealed at
 * once). `notifySplashGlideLanded(targetEl)` fires exactly once — unless the
 * glide is cancelled first (`cancelSplashGlide`).
 */
export function runSplashGlide(sourceEl, targetEl, { onLanded } = {}) {
  const land = () => { notifySplashGlideLanded(targetEl); onLanded?.(); };
  if (!targetEl || typeof document === 'undefined' || prefersReducedMotion()) { land(); return 0; }
  const toEl = targetEl.firstElementChild || targetEl;
  // The same measure for the aim and for the in-flight re-aim (logoJourney).
  const measureTarget = () => layoutRect(toEl);
  const ran = flyLogoJourney(sourceEl, targetEl, measureTarget(), {
    durationMs: glideDurationOf(targetEl), onLand: land, measureTarget,
  });
  if (!ran) land();
  return ran;
}
